import React, { useState, useMemo } from 'react';
import { uniqueId, isEmpty } from 'underscore';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { withErrorView } from '@bingads-webui-campaign-react/smart-page-error-boundary';
import { SmartHeader, useActionButtons } from '@bingads-webui-campaign-react/smart-component-header';
import { PrivacyContentDialog } from '@bingads-webui-campaign-react/smart-component-privacy-policy';

const commonFooterMobileContainerStyle = {
  marginLeft: 11,
  marginRight: 11,
  justifyContent: 'space-around',
  flexFlow: 'wrap',
};

const getStyles = (components = {}, { palette = {} }) => ({
  footerContainer: {
    minHeight: 82,
    lineHeight: '50px',
    borderTop: '1px solid #C4C4C4',
    marginLeft: 33,
    marginRight: 33,
    display: 'flex',
    fontSize: 16,
    color: palette.footer,
    justifyContent: 'space-between',
  },
  footerContainerMobile: {
    ...commonFooterMobileContainerStyle,
    marginBottom: components.fixedMobileFooter.height,
  },
  footerContainerMobileForBlankBanner: {
    ...commonFooterMobileContainerStyle,
  },
  footerContainerMobileForHiddenButton: {
    ...commonFooterMobileContainerStyle,
  },
  link: {
    background: 'none',
    border: 'none',
    textDecoration: 'underline',
    height: 'fit-content',
    fontWeight: '600',
  },
});

export const UnStyledSmartFooterView = ({
  classes,
  i18n,
  id,
  rootId,
  disabled,
  hideFooterButton,
  showPrivacyPolicy,
  isEditMode,
  businessName,
  policyContent,
  isMobile,
}) => {
  const actionButtons = useActionButtons();
  const [showPrivacyPolicyDialog, setShowPrivacyPolicyDialog] = useState(false);

  let footerContainerMobileClass = null;
  if (isMobile) {
    if (hideFooterButton) {
      footerContainerMobileClass = classes.footerContainerMobileForHiddenButton;
    } else if (isEmpty(actionButtons)) {
      footerContainerMobileClass = classes.footerContainerMobileForBlankBanner;
    } else {
      footerContainerMobileClass = classes.footerContainerMobile;
    }
  }

  const privacyLinkConfig = useMemo(() => {
    if (disabled || isEditMode) {
      return { tag: 'span' };
    }

    return { tag: 'button', onClick: () => setShowPrivacyPolicyDialog(true) };
  }, [isEditMode, disabled]);

  return (
    <footer id={id} >
      <div className={classnames(classes.footerContainer, footerContainerMobileClass)}>
        {
          showPrivacyPolicy &&
          <privacyLinkConfig.tag
            className={classnames(classes.link, 'show-privacy-dialog-button')}
            onClick={privacyLinkConfig.onClick}
          >
            {i18n.getString(_TL_('Privacy policy'))}
          </privacyLinkConfig.tag>
        }
        <span>
          {i18n.getString(_TL_('Powered by Microsoft Advertising'))}
        </span>
      </div>
      {
        isMobile && !hideFooterButton &&
        <SmartHeader
          id={uniqueId('mobile_footer_')}
          rootId={rootId}
          disabled={disabled || isEditMode}
          footerSplit
        />
      }
      {
        showPrivacyPolicy && showPrivacyPolicyDialog &&
        <PrivacyContentDialog
          i18n={i18n}
          onClose={() => setShowPrivacyPolicyDialog(false)}
          businessName={businessName}
          value={policyContent}
          isMobile={isMobile}
        />
      }
    </footer>
  );
};

UnStyledSmartFooterView.propTypes = {
  classes: PropTypes.shape({
    footerContainer: PropTypes.string.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  id: PropTypes.string.isRequired,
  rootId: PropTypes.string.isRequired,
  disabled: PropTypes.bool.isRequired,
  hideFooterButton: PropTypes.bool,
  showPrivacyPolicy: PropTypes.bool,
  isEditMode: PropTypes.bool,
  isMobile: PropTypes.bool,
  businessName: PropTypes.string.isRequired,
  policyContent: PropTypes.string,
};

UnStyledSmartFooterView.defaultProps = {
  hideFooterButton: false,
  showPrivacyPolicy: false,
  isEditMode: false,
  isMobile: false,
};

UnStyledSmartFooterView.defaultProps = {
  policyContent: '',
};

export const SmartFooterView = withErrorView(withDefaultStyles(
  UnStyledSmartFooterView,
  getStyles
), 'componentMode');
